/**
 * Pool smart-search contract (BL-207).
 *
 * A business pastes a Job Description; the backend extracts criteria (brain),
 * pre-filters the pool, ranks by semantic cosine + a blended score, and returns
 * ranked candidates each carrying a `matchScore` and structured `matchReasons`.
 * Reasons are STRUCTURED (a code + params), never prose — the frontend renders
 * them via `t()` so non-EN users don't get English (root CLAUDE.md i18n rule).
 */
import { z } from 'zod';

import { iqultureBadgeSchema } from '../heroes/iqulture';

export const poolSmartSearchRequestSchema = z.object({
  /** The pasted Job Description (free text). */
  jobDescription: z.string().trim().min(1, 'business.pool.smartSearch.errors.empty').max(20000),
  /** Optional skill narrowing (edited by the wizard) — OR-matched as a filter. */
  skills: z.array(z.string().trim().min(1).max(80)).max(50).optional(),
  /** Optional location narrowing — OR-matched against the candidate city. */
  locations: z.array(z.string().trim().min(1).max(100)).max(20).optional(),
  /** Optional iQulture badge narrowing — any of these badges (the API's own). */
  iqultureBadges: z.array(iqultureBadgeSchema).max(3).optional(),
  page: z.number().int().min(1).optional(),
  limit: z.number().int().min(1).max(100).optional(),
  /**
   * Ordering of the FULL ranked set before pagination. Defaults to best-match
   * first. `relevance` is an alias for `match-desc` (the server ranking); the
   * others let the business re-order the entire result set, not just one page.
   */
  sort: z
    .enum(['relevance', 'match-desc', 'match-asc', 'iqulture-desc', 'iqulture-asc', 'name'])
    .optional(),
});

export type PoolSmartSearchRequest = z.infer<typeof poolSmartSearchRequestSchema>;

/**
 * `GET /business/pool/search?iqultureBadges=pro,potential` — the browse's badge
 * filter, comma-separated on the wire. An unknown badge is a 400, never a
 * silently ignored filter.
 */
export const poolBadgeFilterQuerySchema = z
  .string()
  .transform((raw) =>
    raw
      .split(',')
      .map((b) => b.trim())
      .filter(Boolean)
  )
  .pipe(z.array(iqultureBadgeSchema).max(3))
  .optional();

/**
 * The keyword/facet BROWSE's orderings — applied by the server across the WHOLE
 * result set before paging (P2.1: the page sorted and paged 2,000 downloaded
 * rows itself). `relevance` is rich profiles first, newest first.
 */
export const POOL_BROWSE_SORTS = ['relevance', 'iqulture-desc', 'iqulture-asc', 'name'] as const;
export type PoolBrowseSort = (typeof POOL_BROWSE_SORTS)[number];

/** Whether a sort key is one the browse understands (the smart view has more). */
export const isPoolBrowseSort = (sort: string): sort is PoolBrowseSort =>
  (POOL_BROWSE_SORTS as readonly string[]).includes(sort);

/** `GET /business/pool/search?sort=…` — an unknown ordering is a 400. */
export const poolBrowseSortQuerySchema = z.enum(POOL_BROWSE_SORTS).optional();

/**
 * `GET /business/pool/search?roles=["Engineer, Backend"]` — the role filter, a
 * JSON array on the wire: a job title may itself contain a comma, so the
 * comma-separated form the skills filter uses would split one role in two.
 * Each role matches a job title exactly (the roles facet's values).
 */
export const poolRolesFilterQuerySchema = z
  .string()
  .transform((raw, ctx) => {
    try {
      return JSON.parse(raw) as unknown;
    } catch {
      ctx.addIssue({ code: 'custom', message: 'roles must be a JSON array of strings' });
      return z.NEVER;
    }
  })
  .pipe(z.array(z.string().trim().min(1).max(200)).max(50))
  .optional();

/**
 * How strong a JD match is — the server's reading of `matchScore`
 * (`classifyPoolMatch`); the card only colours it.
 */
export const POOL_MATCH_BANDS = ['strong', 'good', 'partial'] as const;
export type PoolMatchBand = (typeof POOL_MATCH_BANDS)[number];

/**
 * Why a candidate ranked where it did — structured for i18n. Each variant maps
 * to a `business.pool.matchReason.<code>` key with the given interpolation
 * params.
 */
export type PoolMatchReason =
  | { code: 'semantic'; score: number }
  | { code: 'skills'; matched: string[]; total: number }
  | { code: 'mustHaves'; met: string[]; missing: string[] }
  | { code: 'seniority'; level: string; matched: boolean }
  | { code: 'niceToHaves'; met: string[] }
  | { code: 'readiness'; score: number }
  | { code: 'location'; label: string };

/** Create/update a saved pool search (BL-207) — a named JD + skill filter. */
export const poolSavedSearchCreateSchema = z.object({
  name: z.string().trim().min(1, 'business.pool.savedSearch.errors.name').max(200),
  jobDescription: z.string().trim().min(1, 'business.pool.smartSearch.errors.empty').max(20000),
  skills: z.array(z.string().trim().min(1).max(80)).max(50).default([]),
});

export type PoolSavedSearchCreate = z.infer<typeof poolSavedSearchCreateSchema>;

/** A stored saved search as returned to the business-web UI. */
export interface PoolSavedSearchDto {
  id: string;
  name: string;
  jobDescription: string;
  skills: string[];
  createdAt: string;
}

/** The JD criteria the AI extracted — surfaced so the wizard can show/edit it. */
export interface PoolCriteriaSummary {
  title: string | null;
  seniority: string | null;
  skills: string[];
  location: string | null;
  mustHaves: string[];
  niceToHaves: string[];
}
